import axios from 'axios';
import isEmpty from 'lodash/isEmpty';

import {
  ALL,
  APP_LOADING,
  APP_RESET,
  USERS_DELETE_RESET,
  USERS_GET_SUCCESS,
  USERS_GET_ERROR,
  USER_GET_REQUEST,
  USER_GET_SUCCESS,
  USER_GET_ERROR,
  USER_CREATE_SUCCESS,
  USER_CREATE_ERROR,
  USER_ORGANIZATIONS_FILTERED_SUCCESS,
  USER_ORGANIZATIONS_FILTERED_ERROR,
  USER_UPDATE_SUCCESS,
  USER_UPDATE_ERROR,
  USER_UPDATE_REQUEST,
  USER_UPDATE_RESET,
  USER_REVOKE_ACCESS_REQUEST,
  USER_REVOKE_ACCESS_RESET,
  USER_REVOKE_ACCESS_SUCCESS,
  USER_REVOKE_ACCESS_ERROR,
  USER_RESEND_ACTIVATION_EMAIL_ERROR,
  USER_RESEND_ACTIVATION_EMAIL_SUCCESS,
  USER_RESEND_ACTIVATION_EMAIL_REQUEST,
  USER_DELETE_ERROR,
  USER_DELETE_SUCCESS,
  USER_DELETE_REQUEST,
  USER_BULK_DELETE_REQUEST,
  USER_BULK_DELETE_SUCCESS,
  USER_BULK_DELETE_ERROR,
} from '../../constants';
import { AXIOS_DEFAULT_OPTIONS, AXIOS_DEFAULT_OPTIONS_HEADERS } from '../';
import { getConfig } from '../../reducers/portalConfig';
import { getValidationErrors } from '../../utils';
import { stringifyUrl } from '../../utils/actions';
import { checkSession } from '../portalConfig';

const init = (dispatch, isLoading = true) =>
  dispatch({
    type: APP_LOADING,
    isLoading,
  });

export const resetApp = () => dispatch => dispatch({ type: APP_RESET });
export const resetDeleteNotification = () => dispatch => {
  dispatch({ type: USERS_DELETE_RESET });
};
export const resetRevokeNotification = () => dispatch => {
  dispatch({ type: USER_REVOKE_ACCESS_RESET });
};
export const resetUpdateNotification = () => dispatch => {
  dispatch({ type: USER_UPDATE_RESET });
};
export const showLoading = isLoading => dispatch => init(dispatch, isLoading);

export const fetchUsers = ({
  username,
  name,
  email,
  authConfigUuid,
  roleUuids = [],
  orgUuids,
  status,
  lastLoginFrom,
  lastLoginTo,
  sortBy,
  sortOrder,
  page = 0,
  rowsPerPage = 12,
  isExactMatch = false,
  organizationCheck = 'add',
  includeAnonymous,
} = {}) =>
  async (dispatch, getState) => {
    init(dispatch);
    const config = getConfig(getState());
    const { portal } = config;

    const urlPrefix = `${portal.hostname}/admin/v2/users`;
    const queryObject = {
      authConfigUuid,
      roleUuids: !isEmpty(roleUuids) ? roleUuids.join(',') : undefined,
      orgUuids,
      status: (status !== ALL) ? status : undefined,
      lastLoginStart: lastLoginFrom,
      lastLoginEnd: lastLoginTo,
      sortBy: sortBy || undefined,
      sortOrder: sortOrder || undefined,
      offset: page * rowsPerPage,
      limit: rowsPerPage,
      includeAnonymous: true,
    };

    if (isExactMatch) {
      queryObject.organizationCheck = organizationCheck;
      queryObject.includeAnonymous = includeAnonymous;
      if (email) {
        queryObject.email = email;
      } else if (username) {
        queryObject.username = username;
      }
    } else {
      queryObject.search = !!(name || email || username) || undefined;
      queryObject.field = (name && 'name') || (email && 'email') || (username && 'username') || undefined;
      queryObject.query = name || email || username || undefined;
    }

    const url = stringifyUrl({
      url: urlPrefix,
      query: queryObject,
      options: {
        encode: true,
        skipNull: true,
        arrayFormat: 'comma',
      },
    });
    await axios.get(url, AXIOS_DEFAULT_OPTIONS)
      .then(response =>
        dispatch({
          type: USERS_GET_SUCCESS,
          payload: response.data,
        }),
      )
      .catch(error =>
        dispatch({
          type: USERS_GET_ERROR,
          payload: getValidationErrors(error),
        }),
      );
  };

export const fetchOrganizationsFiltered = (size, pageNumber, name = '') =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/api/${portal.tenantPrefix}/tenant-admin/internal/organizations?status=ENABLED&size=${size}&page=${pageNumber}&name=${name}`;

    await axios.get(url, { credentials: 'include' })
      .then((response) => {
        dispatch({
          type: USER_ORGANIZATIONS_FILTERED_SUCCESS,
          payload: response.data,
        });
      })
      .catch((error) =>
        dispatch({
          type: USER_ORGANIZATIONS_FILTERED_ERROR,
          payload: getValidationErrors(error),
        }),
      );
  };

export const createUser = (userDetails, notifyUser) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/admin/v2/users?notifyUser=${notifyUser}`;

    const isValidSession = await checkSession(getState());
    if (isValidSession) {
      await axios.post(url, userDetails, AXIOS_DEFAULT_OPTIONS_HEADERS)
        .then((response) => {
          dispatch({
            type: USER_CREATE_SUCCESS,
            isLoading: false,
            payload: response.data,
          });
        })
        .catch((error) => {
          dispatch({
            type: USER_CREATE_ERROR,
            payload: getValidationErrors(error),
          });
        });
    } else {
      window.location.href =
        `${portal.hostname}/admin/login?to-default-config=true&referrer=${window.location.pathname}`;
    }
  };

export const updateUser = (userDetails, isRevokingAccess) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/admin/v2/users/${userDetails.uuid}`;

    const isValidSession = await checkSession(getState());
    if (isValidSession) {
      if (isRevokingAccess) {
        dispatch({
          type: USER_REVOKE_ACCESS_REQUEST,
        });
      } else {
        dispatch({
          type: USER_UPDATE_REQUEST,
          isLoading: true,
        });
      }
      await axios.put(url, userDetails, AXIOS_DEFAULT_OPTIONS_HEADERS)
        .then((response) => {
          if (isRevokingAccess) {
            dispatch({
              type: USER_REVOKE_ACCESS_SUCCESS,
            });
          } else {
            dispatch({
              type: USER_UPDATE_SUCCESS,
              isLoading: false,
              payload: response.data,
            });
          }
        })
        .catch((error) => {
          if (isRevokingAccess) {
            dispatch({
              type: USER_REVOKE_ACCESS_ERROR,
            });
          } else {
            dispatch({
              type: USER_UPDATE_ERROR,
              payload: getValidationErrors(error),
            });
          }
        });
    } else {
      window.location.href =
        `${portal.hostname}/admin/login?to-default-config=true&referrer=${window.location.pathname}`;
    }
  };

export const deleteUser = (userUuid) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/admin/v2/users/${userUuid}`;

    const isValidSession = await checkSession(getState());
    if (isValidSession) {
      dispatch({
        type: USER_DELETE_REQUEST,
        isLoading: true ,
      });
      await axios.delete(url, AXIOS_DEFAULT_OPTIONS_HEADERS)
        .then((response) => {
          dispatch({
            type: USER_DELETE_SUCCESS,
            isLoading: false,
            payload: response.data,
          });
        })
        .catch((error) => {
          dispatch({
            type: USER_DELETE_ERROR,
            payload: getValidationErrors(error),
          });
        });
    } else {
      window.location.href =
        `${portal.hostname}/admin/login?to-default-config=true&referrer=${window.location.pathname}`;
    }
  };

  export const resendActivationEmail = (userUuid) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/admin/accountSetup/${userUuid}/resendEmail`;

    const isValidSession = await checkSession(getState());
    if (isValidSession) {
      dispatch({
        type: USER_RESEND_ACTIVATION_EMAIL_REQUEST,
        isLoading: true ,
      });
      await axios.post(url, {} , AXIOS_DEFAULT_OPTIONS_HEADERS)
        .then((response) => {
          dispatch({
            type: USER_RESEND_ACTIVATION_EMAIL_SUCCESS,
            isLoading: false,
            payload: response.data,
          });
        })
        .catch((error) => {
          dispatch({
            type: USER_RESEND_ACTIVATION_EMAIL_ERROR,
            payload: getValidationErrors(error),
          });
        });
    } else {
      window.location.href =
        `${portal.hostname}/admin/login?to-default-config=true&referrer=${window.location.pathname}`;
    }
  };

export const fetchUser = (userId) =>
  async (dispatch, getState) => {
    const config = getConfig(getState());
    const { portal } = config;
    const url = `${portal.hostname}/admin/v2/users/${userId}`;

    const isValidSession = await checkSession(getState());
    if (isValidSession) {
      dispatch({
        type: USER_GET_REQUEST,
        isLoading: true ,
      });
      await axios.get(url, AXIOS_DEFAULT_OPTIONS_HEADERS)
        .then((response) => {
          dispatch({
            type: USER_GET_SUCCESS,
            isLoading: false,
            payload: response.data,
          });
        })
        .catch((error) => {
          dispatch({
            type: USER_GET_ERROR,
            payload: getValidationErrors(error),
          });
        });
    } else {
      window.location.href =
        `${portal.hostname}/admin/login?to-default-config=true&referrer=${window.location.pathname}`;
    }
  };

  export const bulkDeleteUsers = ({ userUuids = [] }) =>
    async (dispatch, getState) => {
      const config = getConfig(getState());
      const { portal } = config;
      const reqUuids = userUuids.join(',');
      const url = `${portal.hostname}/admin/v2/users?uuids=${reqUuids}`;
  
      const isValidSession = await checkSession(getState());
      if (isValidSession) {
        dispatch({
          type: USER_BULK_DELETE_REQUEST,
          isLoading: true ,
        });
        await axios.delete(url, AXIOS_DEFAULT_OPTIONS_HEADERS)
          .then((response) => {
            dispatch({
              type: USER_BULK_DELETE_SUCCESS,
              isLoading: false,
              payload: response.data,
            });
          })
          .catch((error) => {
            dispatch({
              type: USER_BULK_DELETE_ERROR,
              payload: getValidationErrors(error),
            });
          });
      } else {
        window.location.href =
          `${portal.hostname}/admin/login?to-default-config=true&referrer=${window.location.pathname}`;
      }
    };
